import React, { useEffect, useState } from 'react'
import { Box, Divider, Paper, SxProps, Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { UnsubscribeGroupEnum } from '@rsmus/ecp-communicationservice'
import {
  getPaymentRemindersSubscription,
  getPaymentUpdatesSubscription,
  getSubscriptionErrors,
} from '../../store/unsubscribe/emailSubscriptionSlice'
import { Styles } from '../../types'
import SectionHeader from '../layouts/SectionHeader'
import { tokens } from '../../styles/materialTheme'
import EmailPreference from './EmailPreference'

interface EmailNotificationPreferencesProps {
  onPaymentRemindersChange: (value: boolean) => void
  onPaymentUpdatesChange: (value: boolean) => void
  emailPreferencesContainerOverride?: SxProps<Theme> | undefined
}

const styles: Styles = {
  contactPreferencesContainer: {
    flexGrow: 1,
  },
  preferencesDescription: {
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    color: tokens.colors.rsmGray.dividers,
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    font: '1rem Prelo-Book, sans-serif',
  },
}

const EmailNotificationPreferences = ({
  onPaymentRemindersChange,
  onPaymentUpdatesChange,
  emailPreferencesContainerOverride = undefined,
}: EmailNotificationPreferencesProps) => {
  const { t } = useTranslation()
  const paymentRemindersSubscription = useSelector(
    getPaymentRemindersSubscription,
  )
  const paymentUpdatesSubscription = useSelector(getPaymentUpdatesSubscription)
  const subscriptionErrors = useSelector(getSubscriptionErrors)
  const [paymentReminderHasErrors, setPaymentReminderHasErrors] =
    useState(false)
  const [paymentUpdateHasErrors, setPaymentUpdateHasErrors] = useState(false)
  const sectionHeaderStyleOverride = (theme: Theme) => ({
    display: 'flex',
    marginBottom: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    fontWeight: 700,
    textTransform: 'uppercase',
    '& hr': {
      flexShrink: 1,
      alignSelf: 'flex-end',
      marginBottom: '0.125rem',
      marginLeft: '0.5rem',
      borderColor: theme.palette.primary.main,
      width: '100%',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: undefined,
      lineHeight: '1.5rem',
    },
  })

  useEffect(() => {
    if (subscriptionErrors.length > 0) {
      setPaymentReminderHasErrors(
        subscriptionErrors.includes(UnsubscribeGroupEnum.PaymentReminders),
      )
      setPaymentUpdateHasErrors(
        subscriptionErrors.includes(UnsubscribeGroupEnum.PaymentUpdates),
      )
    } else {
      setPaymentReminderHasErrors(false)
      setPaymentUpdateHasErrors(false)
    }
  }, [subscriptionErrors])

  return (
    <Paper
      sx={{ marginBottom: '1rem' }}
      component="section"
      data-testid="Sec_Profile_EmailNotificationPreferences">
      <SectionHeader
        overridableSxProps={sectionHeaderStyleOverride}
        title="email notification preferences"
        testId="Hed_Profile_EmailNotificationPreferences"
      />
      <Box
        sx={
          emailPreferencesContainerOverride || styles.emailPreferencesContainer
        }>
        <Box sx={styles.preferencesDescription}>
          {t('Unsubscribe.EmailPreferencesDescription')}
        </Box>
        <Divider />
        <EmailPreference
          preferenceName="PaymentReminders"
          preferenceHeader="Unsubscribe.PaymentReminders"
          preferenceDescription="Unsubscribe.paymentRemindersDescription"
          defaultPreferenceValue={paymentRemindersSubscription}
          hasErrors={paymentReminderHasErrors}
          onPreferenceChange={onPaymentRemindersChange}
        />
        <Divider />
        <EmailPreference
          preferenceName="PaymentUpdates"
          preferenceHeader="Unsubscribe.PaymentUpdates"
          preferenceDescription="Unsubscribe.PaymentUpdatesDescription"
          defaultPreferenceValue={paymentUpdatesSubscription}
          hasErrors={paymentUpdateHasErrors}
          onPreferenceChange={onPaymentUpdatesChange}
        />
        <Divider />
      </Box>
    </Paper>
  )
}

export default EmailNotificationPreferences
